import { User } from '@prisma/client'
import { Divider, Form, Radio, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { AppSessionType } from '~/features/appSession/common'
import { ProviderAccountAuthorization } from '~/features/formConfiguration/client/components/CustomInputs/ProviderAccountAuthorization'
import { SelectGooglePicker } from '~/features/formConfiguration/client/components/CustomInputs/SelectGooglePicker'
import { SelectOneDrivePicker } from '~/features/formConfiguration/client/components/CustomInputs/SelectOneDrivePicker'
import { SelectProviderAccount } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAccount'
import { Extensions } from '~/plugins/data-connector/providers/oneDrive/common'
import { Toolbox } from '~/plugins/toolbox/shared'
import { OnboardingValues } from '../../common'

type Props = {
  user: User
  appSession: AppSessionType
}

export const Settings: React.FC<Props> = ({ user, appSession }) => {
  const values = (appSession?.values?.onboarding ?? {}) as OnboardingValues

  const valuesSafe = {
    ...values,
  } as OnboardingValues

  const logic = CommonClient.Form.useLogic<OnboardingValues>({
    appSession,
    cleanValues(values) {
      const isOneDrive = values.fileServices?.provider === 'oneDrive'

      return {
        ...values,
        googleDriveSettings: isOneDrive ? {} : values.googleDriveSettings,
        oneDriveSettings: isOneDrive ? values.oneDriveSettings : {},
      }
    },
  })

  const isGoogleDriveFilesUpdated = (valuesUpdated: OnboardingValues) => {
    const filesBefore = values.googleDriveSettings?.files ?? []
    const filesAfter = valuesUpdated.googleDriveSettings?.files ?? []

    const fileIdsBefore = filesBefore.map(file => file.id)
    const fileIdsAfter = filesAfter.map(file => file.id)

    return !Toolbox.UtilityItems.isEqual<string>(fileIdsBefore, fileIdsAfter)
  }

  const isOneDriveFilesUpdated = (valuesUpdated: OnboardingValues) => {
    const filesBefore = values.oneDriveSettings?.files ?? []
    const filesAfter = valuesUpdated.oneDriveSettings?.files ?? []

    const fileIdsBefore = filesBefore.map(file => file.id)
    const fileIdsAfter = filesAfter.map(file => file.id)

    return !Toolbox.UtilityItems.isEqual<string>(fileIdsBefore, fileIdsAfter)
  }

  const handleSubmit = async (valuesUpdated: OnboardingValues) => {
    if (isGoogleDriveFilesUpdated(valuesUpdated)) {
      await logic.triggerHttpEvent('onboarding.googleDriveSettings.completed')
    }

    if (isOneDriveFilesUpdated(valuesUpdated)) {
      await logic.triggerHttpEvent('onboarding.oneDriveSettings.completed')
    }
  }

  return (
    <>
      <CommonClient.Form.Wrapper
        {...logic}
        onSubmit={handleSubmit}
        values={valuesSafe}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Drive
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label="Drive"
          description="Drive provider connected"
        >
          <Form.Item
            name={['fileServices', 'provider']}
            rules={[{ required: true }]}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  label: 'Google Drive',
                  value: 'googleDrive',
                },
                {
                  label: 'Microsoft OneDrive',
                  value: 'oneDrive',
                },
              ]}
            />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue(['fileServices', 'provider'])
            if (provider === 'googleDrive') {
              return (
                <Form.Item
                  name={['fileServices', 'accountGoogleDrive']}
                  label="Google Drive Account"
                  rules={[{ required: true }]}
                >
                  <SelectProviderAccount providerKey="googleDrive" />
                </Form.Item>
              )
            }

            if (provider === 'oneDrive') {
              return (
                <Form.Item
                  name={['fileServices', 'accountOneDrive']}
                  label="OneDrive Account"
                  rules={[{ required: true }]}
                >
                  <SelectProviderAccount providerKey="oneDrive" />
                </Form.Item>
              )
            }
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue(['fileServices', 'provider'])

            const providerAccountId = options.getFieldValue([
              'fileServices',
              'accountGoogleDrive',
            ])

            if (provider !== 'googleDrive' || !providerAccountId) {
              return <></>
            }

            return (
              <>
                <Typography.Title
                  level={4}
                  style={{ margin: 0, marginTop: 32 }}
                >
                  Files
                </Typography.Title>

                <Divider className="my-3" />

                <Form.Item
                  name={['googleDriveSettings', 'permissionManageFiles']}
                  rules={[
                    {
                      validator: (_, value) =>
                        value?.[0]?.isCorrupted
                          ? Promise.reject()
                          : Promise.resolve(),
                      message: 'Authorization is not valid',
                    },
                  ]}
                >
                  <ProviderAccountAuthorization
                    providerAccountId={providerAccountId}
                    scope="https://www.googleapis.com/auth/drive.file"
                    ui={{
                      title: 'Access Drive Files',
                      iconAntd: 'GoogleOutlined',
                      description:
                        'Allow access to selected Google Drive files only',
                    }}
                  />
                </Form.Item>
              </>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue(['fileServices', 'provider'])

            const providerAccountId = options.getFieldValue([
              'fileServices',
              'accountGoogleDrive',
            ])

            const permission = options.getFieldValue([
              'googleDriveSettings',
              'permissionManageFiles',
            ])

            if (
              !permission ||
              !providerAccountId ||
              provider !== 'googleDrive'
            ) {
              return <></>
            }

            return (
              <Form.Item name={['googleDriveSettings', 'files']}>
                <SelectGooglePicker
                  isMultiple
                  providerAccountId={providerAccountId}
                  providerAccountScopeValues={permission}
                  buttonEmptyPlaceholder="Choose files"
                />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue(['fileServices', 'provider'])

            const providerAccountId = options.getFieldValue([
              'fileServices',
              'accountOneDrive',
            ])

            if (!providerAccountId || provider !== 'oneDrive') {
              return <></>
            }

            return (
              <>
                <Typography.Title
                  level={4}
                  style={{ margin: 0, marginTop: 32 }}
                >
                  Files
                </Typography.Title>

                <Divider className="my-3" />
                <Form.Item
                  name={['oneDriveSettings', 'files']}
                  label="Legal documents"
                >
                  <SelectOneDrivePicker
                    providerAccountId={providerAccountId}
                    isMultiple={true}
                    buttonEmptyPlaceholder="Choose files"
                    extensions={[
                      Extensions.DOC,
                      Extensions.DOCX,
                      Extensions.XLS,
                      Extensions.XLSX,
                      Extensions.CSV,
                      Extensions.PDF,
                      Extensions.MSG,
                    ]}
                  />
                </Form.Item>
              </>
            )
          }}
        </Form.Item>
      </CommonClient.Form.Wrapper>
    </>
  )
}
